import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, RefreshCw, ArrowLeft, Home, Mail, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/Button";

interface ErrorPageProps {
  code?: number;
  title?: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const defaults: Record<number, { icon: typeof AlertTriangle; title: string; description: string }> = {
  400: { icon: AlertTriangle, title: "Bad request", description: "Something in your request didn't look right. Please check your input and try again." },
  403: { icon: ShieldAlert, title: "Access denied", description: "You don't have permission to do that. This might be due to a suspension or verification requirement." },
  404: { icon: Mail, title: "Not found", description: "What you're looking for doesn't exist or has been moved." },
  429: { icon: AlertTriangle, title: "Too many requests", description: "You've hit a rate limit. Please wait a moment and try again." },
  500: { icon: AlertTriangle, title: "Server error", description: "Something went wrong on our end. We've been notified and are looking into it." },
  502: { icon: AlertTriangle, title: "Bad gateway", description: "Our upstream service is temporarily unavailable. Please try again shortly." },
  503: { icon: AlertTriangle, title: "Service unavailable", description: "The service is temporarily down for maintenance. We'll be back shortly." },
};

export function ErrorPage({ code, title, description, action }: ErrorPageProps) {
  const navigate = useNavigate();
  const codeNum = code || 500;
  const def = defaults[codeNum] || defaults[500];
  const Icon = def.icon;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-ink-50 dark:bg-ink-950">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="text-center max-w-md"
      >
        <div className="inline-flex size-20 items-center justify-center rounded-3xl bg-gradient-to-br from-brand-500 to-fuchsia-500 text-white shadow-xl shadow-brand-500/30 mb-8">
          <Icon className="size-9" />
        </div>

        {codeNum && (
          <p className="text-2xs font-semibold uppercase tracking-widest text-ink-400 mb-2">
            Error {codeNum}
          </p>
        )}

        <h1 className="font-display text-3xl font-semibold tracking-tight text-ink-900 dark:text-ink-100">
          {title || def.title}
        </h1>

        <p className="mt-3 text-sm text-ink-600 dark:text-ink-400 text-pretty leading-relaxed">
          {description || def.description}
        </p>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          {action ? (
            <Button variant="primary" leftIcon={<RefreshCw className="size-4" />} onClick={action.onClick}>
              {action.label}
            </Button>
          ) : (
            <Button variant="primary" leftIcon={<RefreshCw className="size-4" />} onClick={() => window.location.reload()}>
              Try again
            </Button>
          )}
          <Link to="/">
            <Button variant="outline" leftIcon={<Home className="size-4" />}>Home</Button>
          </Link>
          <button onClick={() => navigate(-1)}>
            <Button variant="ghost" leftIcon={<ArrowLeft className="size-4" />}>Go back</Button>
          </button>
        </div>
      </motion.div>
    </div>
  );
}
